'use client';

import { useState } from 'react';
import { Button, Modal, StatusIcon, toast } from '@mokoboko/components';

import { getNoCreditReturnedReason } from '@/helpers/credits';
import { removeGuestBody } from '@/helpers/guest-pass';
import { AnalyticsEvent, track } from '@/lib/analytics/tracker';
import { ERROR_STATUS_ICON_CLASS } from '@/lib/error-ramp';
import { getErrorCode, getErrorMessage } from '@/lib/error-utils';
import { isLoginRequired } from '@/lib/http-adapter';
import type { ReceiptGuest } from '@/lib/types';
import { removeOrderGuest } from '@/services/order';

export const RemoveGuestModal = ({
  orderId,
  guest,
  hostBooked,
  passReturns,
  classId,
  className,
  isFromPack,
  onClose,
  onRecheck,
  onLoginRequired,
}: {
  orderId: string;
  guest: ReceiptGuest;
  hostBooked: boolean;
  passReturns: boolean;
  classId: string;
  className: string;
  isFromPack: boolean;
  onClose: () => void;
  onRecheck: () => void;
  onLoginRequired: () => void;
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleRemove = async () => {
    try {
      setIsProcessing(true);
      const result = await removeOrderGuest(orderId, guest.guest_order_id);
      const passReturned = result?.pass_returned ?? false;
      track({
        event: AnalyticsEvent.GUEST_REMOVED,
        properties: {
          class_id: classId,
          class_name: className,
          pass_returned: passReturned,
        },
      });
      toast.success(`${guest.first_name} removed`, {
        description: passReturned
          ? '1 guest pass returned.'
          : `No guest pass returned. ${getNoCreditReturnedReason(false, isFromPack)}`,
      });
      onClose();
      onRecheck();
    } catch (error) {
      if (isLoginRequired(error)) {
        onLoginRequired();
        return;
      }
      const code = getErrorCode(error);
      if (code === 'GUEST_ALREADY_CANCELLED' || code === 'GUEST_NOT_FOUND') {
        onClose();
        onRecheck();
        toast.error(`${guest.first_name} is no longer booked.`);
        return;
      }
      if (
        code === 'CLASS_ALREADY_STARTED' ||
        code === 'BOOKING_RULE_VIOLATION'
      ) {
        onClose();
        onRecheck();
        toast.error(
          getErrorMessage(error, "This guest can't be removed any more.")
        );
        return;
      }
      toast.error("We couldn't remove your guest. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal.Root
      open
      onOpenChange={(open) => {
        if (!open && !isProcessing) onClose();
      }}
    >
      <Modal.Content size="sm" hasCloseButton={false}>
        <Modal.Header className="border-b-0">
          <div className="mb-3">
            <StatusIcon
              variant="error"
              size="xl"
              className={ERROR_STATUS_ICON_CLASS}
            />
          </div>
          <Modal.Title>Remove {guest.first_name} from this class?</Modal.Title>
          <Modal.Description>
            {removeGuestBody({
              firstName: guest.first_name,
              hostBooked,
              passReturns,
            })}
          </Modal.Description>
        </Modal.Header>
        <Modal.Footer>
          <Modal.Close asChild>
            <Button
              variant="secondary-gray"
              onClick={onClose}
              disabled={isProcessing}
              className="ml-auto"
            >
              Keep guest
            </Button>
          </Modal.Close>
          <Button
            variant="primary"
            onClick={handleRemove}
            isProcessing={isProcessing}
          >
            Remove guest
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
};
